import { Injectable } from "@angular/core";
import { catchError, EMPTY, filter, first, map, Observable } from "rxjs";
import { GoogleMappingService } from "../google-mapping/google-mapping.service";

/** Plaid package that only allows a limited number of free transaction imports. */
const FREE_PLAID_PACKAGE_ID = 2;

/** Balance an organisation is expected to stay above, per package type. */
const FREE_LOW_BALANCE_THRESHOLD = 50;
const PAID_LOW_BALANCE_THRESHOLD = 250;

export interface ICcPlaidPackageBalance {
  /** False when the organisation has no plaid package at all — it has to subscribe. */
  hasPackage: boolean;
  /** Transactions the organisation still has. */
  remaining: number;
  /** True when the organisation is on the free package. */
  isFreePackage: boolean;
}

export interface ICcTransactionsLowBalance extends ICcPlaidPackageBalance {
  /** True when the balance has dropped below the threshold for the organisation's package. */
  isLowBalance: boolean;
  /** Balance the organisation is expected to stay above. */
  threshold: number;
}

@Injectable({ providedIn: "root" })
export class CcFreeTransactionsService {
  constructor(private googleMappingService: GoogleMappingService) {}

  /**
   * Reads the organisation's plaid package balance. An empty response means no
   * package has been bought, which callers surface as "subscribe". Emits nothing
   * when the call itself fails, so callers stay put on an unknown allowance.
   */
  fnGetPlaidPackageBalance(
    organisationId: number
  ): Observable<ICcPlaidPackageBalance> {
    return this.googleMappingService
      .GetOrganisationPlaidPackage(organisationId)
      .pipe(
        first(),
        filter((data) => !!(data && data.statusCode == 0)),
        map((data) => {
          if (!data.responseInfo) {
            return { hasPackage: false, remaining: 0, isFreePackage: false };
          }
          return {
            hasPackage: true,
            remaining:
              data.responseInfo.totalTransactions -
              data.responseInfo.usedTransactions,
            isFreePackage:
              data.responseInfo.plaidPackageId == FREE_PLAID_PACKAGE_ID,
          };
        }),
        catchError(() => EMPTY)
      );
  }

  /**
   * Whether importing `transactionCount` transactions would go past the free
   * allowance. Only the free package is capped — paid packages import the whole batch.
   */
  fnIsFreeLimitExceeded(
    balance: ICcPlaidPackageBalance,
    transactionCount: number
  ): boolean {
    return balance.isFreePackage && balance.remaining < transactionCount;
  }

  /**
   * Whether the organisation is running low on transactions: below 50 on the free
   * package, below 250 on a paid one. An organisation with no package is not "low",
   * it is unsubscribed — check `hasPackage` first.
   */
  fnGetTransactionsLowBalance(
    organisationId: number
  ): Observable<ICcTransactionsLowBalance> {
    return this.fnGetPlaidPackageBalance(organisationId).pipe(
      map((balance) => {
        const threshold = balance.isFreePackage
          ? FREE_LOW_BALANCE_THRESHOLD
          : PAID_LOW_BALANCE_THRESHOLD;
        return {
          ...balance,
          threshold: threshold,
          isLowBalance: balance.hasPackage && balance.remaining < threshold,
        };
      })
    );
  }
}
